import { RequestForCard } from "@tsTypes/index";
import { LaunchableRequestStatus } from "@tsTypes/requests";
import { MarketplaceRequest } from "@tsTypes/requests/marketplace";
import { RfpPartnershipModel, RfpStatus } from "@tsTypes/rfps";
import dayjs from "dayjs";
import { getLaunchableRequestStatus } from "./requestUtils";

/**
 * @deprecated (justinpchang) Use getLaunchableRequestStatus instead
 */
export const getRfpStatus = (request) => {
  const isBeforeLaunchDate = dayjs().isBefore(request.requestable.launch_date ?? 0);
  const isPastDeadline =
    Boolean(request.requestable.deadline) && dayjs().isAfter(request.requestable.deadline ?? 0);

  if (isBeforeLaunchDate) return RfpStatus.COMING_SOON;
  if (request.enabled)
    if (isPastDeadline) return RfpStatus.IN_EXTENSION;
    else return RfpStatus.ACTIVE;
  return RfpStatus.CLOSED;
};

export const getRfpStatusText = (request, { comingSoon, active, inExtension, pastDeadline }) => {
  switch (getLaunchableRequestStatus(request)) {
    case LaunchableRequestStatus.COMING_SOON: {
      return comingSoon;
    }
    case LaunchableRequestStatus.ACTIVE: {
      return active;
    }
    case LaunchableRequestStatus.IN_EXTENSION: {
      return inExtension;
    }
    case LaunchableRequestStatus.CLOSED: {
      return pastDeadline;
    }
    default: {
      return null;
    }
  }
};

export const sortRfps = <T extends RequestForCard | MarketplaceRequest>(
  rfps: T[],
  withDisplayPriority = false
): T[] => {
  const sortByDisplayPriority = (_rfps: T[]) =>
    _rfps.sort(
      (a, b) =>
        ("display_priority" in b.requestable ? b.requestable.display_priority : 0) -
          ("display_priority" in a.requestable ? a.requestable.display_priority : 0) ||
        new Date(a.requestable.deadline!).getTime() - new Date(b.requestable.deadline!).getTime() ||
        new Date(a.requestable.launch_date!).getTime() -
          new Date(b.requestable.launch_date!).getTime()
    );

  const activeRfps = rfps.filter((rfp) => getRfpStatus(rfp) === RfpStatus.ACTIVE);
  if (withDisplayPriority) sortByDisplayPriority(activeRfps);
  else
    activeRfps.sort(
      (a, b) =>
        new Date(a.requestable.deadline!).getTime() - new Date(b.requestable.deadline!).getTime()
    );

  const comingSoonRfps = rfps.filter((rfp) => getRfpStatus(rfp) === RfpStatus.COMING_SOON);
  if (withDisplayPriority) sortByDisplayPriority(comingSoonRfps);
  else
    comingSoonRfps.sort(
      (a, b) =>
        new Date(a.requestable.launch_date!).getTime() -
        new Date(b.requestable.launch_date!).getTime()
    );

  const pastDeadlineRfps = rfps.filter((rfp) => getRfpStatus(rfp) === RfpStatus.IN_EXTENSION);
  if (withDisplayPriority) sortByDisplayPriority(pastDeadlineRfps);
  else
    pastDeadlineRfps.sort(
      (a, b) =>
        new Date(b.requestable.deadline!).getTime() - new Date(a.requestable.deadline!).getTime()
    );

  const inactiveRfps = rfps.filter((rfp) => getRfpStatus(rfp) === RfpStatus.CLOSED);
  if (withDisplayPriority) sortByDisplayPriority(inactiveRfps);
  else
    inactiveRfps.sort(
      (a, b) =>
        new Date(b.requestable.deadline!).getTime() - new Date(a.requestable.deadline!).getTime()
    );

  return [...activeRfps, ...comingSoonRfps, ...pastDeadlineRfps, ...inactiveRfps];
};

// Partnership model options have changed several times over the years, and existing RFPs may have
// options that are no longer in the current set, so we need to filter those out
export const filterByValidPartnershipModels = (partnershipModels: string[]) => {
  return partnershipModels.filter((partnershipModel) =>
    Object.values(RfpPartnershipModel).includes(partnershipModel as RfpPartnershipModel)
  );
};
